import type { Printing } from "@webscan/catalogue.js";
import type { Candidate, Verdict } from "@webscan/decide.js";
import type { SourceWindow, ViewRect } from "../shared/geometry";
import { link, type Endpoint } from "./bus";

/**
 * The contract between the scanner and the view.
 *
 * Two sides, and nothing else. The **scanner** (`src/scanner/`) is the camera, the
 * detector, the handheld gate, the warp and the identification funnel: pixels in,
 * verdicts out, and no idea what a screen is. The **view** (`src/view/`) is every
 * element on the page: the overlay canvas, the HUD, the find log, the panels, the
 * gestures — and no idea what a detector is. Neither imports the other; ESLint refuses
 * it. Everything that crosses between them is one of the events below, carried by the
 * typed link in `bus.ts`, and this file is the whole of what either side may assume
 * about the other.
 *
 * Two things are deliberately *not* on the bus, because they are stores rather than
 * conversations and both sides simply read them: the settings (`shared/settings.ts`, a
 * store with its own change listener that the panel writes and the funnel reads per
 * card) and the captures (`shared/captures.ts`, the IndexedDB record the scanner writes
 * and the history panel lists). The vendored `@webscan/*` modules and their `gallery`
 * are a shared library in the same sense — the history panel reads catalogue prices out
 * of the index the scanner loaded.
 *
 * ## Who owns the framing
 *
 * The user's window into the sensor — pan, zoom, rotation, desk view, the detection
 * area — is view state: gestures move it and the overlay draws it. The scanner is told
 * what to scan through, via {@link ViewEvents.framing}, and answers every detection with
 * the window it detected through, via {@link ScannerEvents.frame}. Both sides therefore
 * work from the same `SourceWindow`, which is the invariant the whole design rests on:
 * the preview mapping stays fixed while a sensor-clamped overscan area may extend beyond
 * it. Quads remain in window coordinates, including negative ones, and project onto the
 * screen with one scale per axis.
 *
 * ## Who owns the camera
 *
 * The scanner: it asks for the stream, attaches it to the decoder surface it was handed
 * at construction, and releases it. The view owns that same `<video>` element as a thing
 * on the page — it positions it under the overlay with a CSS transform — and learns the
 * sensor raster from {@link ScannerEvents."session:started"}. The scanner never styles
 * the element; the view never touches its stream.
 */

/** What one read of a warped card came back as — the funnel's answer. */
export interface Identification {
  verdict: Verdict;
  reason: string;
  candidates: Candidate[];
  /** The best candidate's printing, or null when nothing survived retrieval. */
  printing: Printing | null;
  /** Whether ORB actually ran. A false here is why a card may read as ambiguous. */
  verified: boolean;
  /**
   * True when ORB was skipped because it could not have changed the verdict, rather than
   * because it was off or unavailable — the two are opposite kinds of unverified, and
   * only one of them is a caveat worth printing next to a find.
   */
  settled: boolean;
  /** True when the 180-degree rotation retrieved better than the upright reading. */
  flipped: boolean;
}

/** A card the scanner is willing to name: ACCEPTED or AMBIGUOUS, never a rejection. */
export interface ScanEvent {
  /** Unique read, retained when collector-only refinement updates this row. */
  readId?: string;
  /** Latest detection of this placement at arrival, for a bounded confirmation glow. */
  confirmation?: Frame;
  identification: Identification;
  /** A stable key for the printing, used to suppress repeats. */
  key: string;
  /**
   * The warped 488x680 RGBA raster the verdict was read from, as warped — when
   * `identification.flipped` the readable orientation is the 180° rotation.
   *
   * An owned copy: reads run detached from the frame loop, so the loop snapshots the
   * shared GPU readback buffer at dispatch. Consumers may keep it.
   */
  card: Uint8ClampedArray;
  /**
   * The upright JPEG, once encoding finishes. iOS uses the canonical raster; other
   * devices can supply a higher-resolution warp. Resolves to null if encoding fails.
   * The result is emitted before encoding starts; this promise never rejects.
   */
  capture: Promise<Blob | null>;
  /**
   * The stored keepsake's id, once the store has answered — the handle a review resolves
   * an ambiguous read through (`resolveCapture` in `shared/captures.ts`). Negative for a
   * row a refusing browser could only keep in memory; null when there was no capture to
   * store. Never rejects. `found` does not wait for the write: an ambiguous card must
   * never hold up the next one, and the id is only needed once someone resolves it.
   */
  captureId: Promise<number | null>;
  /**
   * The detection quad the read was warped from, in uniform *flat window* units —
   * both axes over the keystone span, so shapes survive the normalisation. Measured on
   * the raw trapezoid geometry whatever correction is active, which is what lets the
   * desk-view auto calibration read an absolute tilt from it rather than a delta on
   * the current slider.
   */
  quadFlat: Float32Array;
  /** The window's width over its keystone span — the x extent of those units. */
  windowAspect: number;
}

/**
 * A request to the camera track itself — torch, focus, exposure, white balance, the
 * sensor's own zoom: everything `applyConstraints` can change on an open stream without
 * reopening it. Only the scanner holds the track, so the view asks and the scanner
 * applies; the answer is what the track reports afterwards, never what was asked for.
 *
 * Every property is optional and unknown ones are not declared: a camera exposes what it
 * exposes, and `camera:capabilities` says which of these this one will take.
 */
export interface CameraControlRequest {
  torch?: boolean;
  /** `"continuous"`, `"single-shot"`, `"manual"` or `"none"`, as the track offers them. */
  focusMode?: string;
  /** Meaningful in manual focus only; sent with the mode by callers that know that. */
  focusDistance?: number;
  exposureMode?: string;
  exposureCompensation?: number;
  /** Manual exposure only; the unit is the track's (Chrome: hundreds of microseconds). */
  exposureTime?: number;
  iso?: number;
  whiteBalanceMode?: string;
  colorTemperature?: number;
  /** The sensor's own zoom, distinct from the view's digital window. */
  zoom?: number;
  brightness?: number;
  contrast?: number;
  saturation?: number;
  sharpness?: number;
  frameRate?: number;
  /** Where to focus and meter: fractions of the sensor frame, origin top-left. */
  pointsOfInterest?: { x: number; y: number }[];
  /** Put every controllable property back to what the track opened with. */
  reset?: boolean;
}

/** A numeric capability: what the track will accept, and at what granularity. */
export interface CapabilityRange {
  min: number;
  max: number;
  step?: number;
}

/**
 * One track capability as the browser reports it, reduced to something JSON can carry:
 * a range, a list of modes, or a scalar (Chrome reports `torch: true` for "supported").
 */
export type CameraCapability = CapabilityRange | string[] | boolean[] | string | number | boolean;
export type CameraCapabilities = Record<string, CameraCapability>;
/** `getSettings()` as reported, minus the device identifiers. */
export type CameraSettings = Record<string, string | number | boolean>;

/** What the scanner is to look through, and where in it the detector is to look. */
export interface Framing {
  /** The sensor window on screen; the space every quad comes back in. */
  window: SourceWindow;
  /** The detection area within it, in window pixels — what the detector is letterboxed from. */
  area: ViewRect;
  /** The drawn area's size as fractions of the view, for the log; 1 x 1 when uncropped. */
  crop: { w: number; h: number };
}

/** One detection pass, as the overlay wants it. */
export interface Frame {
  /** When the frame was pulled off the camera — what the quads describe, and what any extrapolation is measured from. */
  at: number;
  /** The window the quads are expressed in. */
  window: SourceWindow;
  /** Every card the detector found, best first; empty when it found none. */
  quads: Float32Array[];
  /** The gate's stillness call for the leading quad. False when there is none. */
  still: boolean;
}

/**
 * What the scanner is doing right now, as a category a design can key an icon, a colour
 * or an animation on. The `text` beside it is the human line and is free to change; the
 * kind is the stable part.
 */
export type StatusKind =
  | "closer"
  /** No card in frame. */
  | "looking"
  /** A card is tracked and settling; the text counts the hold. */
  | "holding"
  /** Waiting for a sharper frame before spending a read. */
  | "focusing"
  /** A read is in flight. */
  | "reading"
  /** The read was rejected; another attempt on the same placement is coming. */
  | "retrying"
  /** A card was named; the text is the name, with a "?" when ambiguous. `found` follows. */
  | "found"
  /** Named again inside the repeat window; nothing was logged. */
  | "repeat"
  /** This placement has been read; waiting for the next card. */
  | "done"
  /** Every attempt on this placement was rejected; the card has to move. */
  | "exhausted"
  /** An iteration or a read failed; the loop goes on. */
  | "error"
  /** The inference engine is gone; only a reload helps. */
  | "crashed"
  /**
   * The device has paused capture under the session — iOS interrupts the camera for a
   * call or for Siri — and the loop is sitting it out. Ends with the next `looking`.
   */
  | "paused";

/** The four public boot tasks; filenames remain technical detail only. */
export type BootTaskId = "detector" | "recogniser" | "index" | "extras";
export interface BootGroup {
  id: BootTaskId;
  label: string;
  optional: boolean;
  status: "queued" | "loading" | "ready" | "failed";
  loaded: number;
  total: number;
}
export interface BootState {
  /** Actual required-asset bytes. Optional downloads never hold the aggregate below 100%. */
  loaded: number;
  total: number;
  fromCache: boolean;
  tasks: BootGroup[];
}

/**
 * Why a session ended. The scanner reports what it knows; what to do about it — offer
 * Start, or bring the camera back by itself — is the view's policy.
 */
export type StopReason =
  /** The view asked, via `stop`. */
  | "requested"
  /** The document went hidden; nothing scans what nobody is watching. */
  | "hidden"
  /**
   * The camera track ended under the session: another app took the camera, the
   * browser's own camera control stopped it, or the device reclaimed it. Releasing the
   * camera ourselves never reports this way.
   */
  | "camera-lost";

/** Scanner → view. */
export interface ScannerEvents {
  /** Aggregate byte progress and the four user-facing task states. */
  "boot:state": BootState;
  /** A retry began, retaining completed downloads and model sessions. */
  "boot:retrying": void;
  /** A boot task began; `id` keys the progress that follows. */
  "boot:task": { id: number; label: string };
  "boot:progress": { id: number; loaded: number; total: number };
  "boot:done": { id: number; note?: string };
  "boot:failed": { id: number; message: string };
  /** Everything a frame needs is in memory; the camera may be started. */
  "boot:ready": { backend: string; warnings: string[] };
  /** Boot failed outright; there is no scanner. */
  "boot:error": { message: string };

  /** A live preview, which may precede model readiness and the recognition loop. */
  "camera:opened": { sensorWidth: number; sensorHeight: number };

  /**
   * The camera is open and the loops are running. The view answers with a `framing` at
   * once — the scanner's first frame waits on it.
   */
  "session:started": { sensorWidth: number; sensorHeight: number };
  /** The camera is released and the loops have ended; `reason` says who asked. */
  "session:stopped": { reason: StopReason };
  /** The camera would not open; the session never started. */
  "camera:failed": { message: string };
  /** The stream changed raster mid-session — some handsets renegotiate on rotation. */
  "sensor:resized": { sensorWidth: number; sensorHeight: number };
  /**
   * The current framing cannot be honoured: a keystone was asked for and only the
   * WebGL sampler can produce one. The view is expected to drop it and re-frame.
   */
  "framing:rejected": { reason: "keystone" };

  /** One detection pass. Arrives at the detector's rate, up to the cap. */
  frame: Frame;
  /** What the HUD should say, and what kind of moment it is. */
  status: { text: string; kind: StatusKind };
  /**
   * Every read that identified a card — accepted or ambiguous, and *before* the repeat
   * suppression, because a re-scan of the same card is new geometric evidence even
   * when it is old news for the log. This is the desk-view calibration feed.
   */
  "read:identified": { quadFlat: Float32Array; windowAspect: number };
  /** A card for the log: identified, and not a repeat. */
  found: ScanEvent;
  /** Better printing evidence for an existing find; no arrival feedback or new image. */
  "found:updated": { readId: string; identification: Identification };
  /** Full detector-pass timings, excluding measured time waiting for the ORT lock. */
  "detector:timing": {
    at: number;
    grabMs: number;
    detectMs: number;
    packMs: number;
    inferMs: number;
    queueMs: number;
    frameMs: number;
    ceilingFps: number;
    targetFps: number;
    gpu: boolean;
  };

  /**
   * A settled card was warped and its sharpness measured — before any read is spent on
   * it. `deferred` is true when the crop was under the session's bar and the attempt was
   * held back for a sharper frame. Telemetry for anything that tunes the camera; it
   * changes no verdict.
   */
  "read:sampled": {
    at: number;
    /** The warp's gradient energy over its variance (see `focusScore`). */
    focus: number;
    /** What it had to clear: a fraction of the session's decaying best. */
    bar: number;
    deferred: boolean;
    /** How tall the card stood in the scan region, in window pixels. */
    cardHeight: number;
    /** The canonical raster over that height: above ~2 the read is looking at interpolation. */
    upscale: number;
  };
  /** One identification came back, whatever the verdict, and what it cost. */
  "read:done": {
    at: number;
    verdict: Verdict;
    reason: string;
    ms: number;
    /** Warp, focus measurement and collector capture for the selected sample. */
    prepareMs: number;
    /** Camera sample to verdict, including the fresh-frame sharpness selection. */
    latencyMs: number;
  };

  /**
   * What the open camera track can do and what it is currently set to. Sent once after
   * `session:started`, and again on `camera:query`. `initial` is the track's settings
   * at open — the reference a `reset` returns to.
   */
  "camera:capabilities": {
    label: string;
    capabilities: CameraCapabilities;
    settings: CameraSettings;
    initial: CameraSettings;
  };
  /**
   * The outcome of a `camera:control`. `ok` means every requested property reads back
   * as asked — an `advanced` constraint set that cannot be satisfied is dropped silently
   * by the browser, so the only proof is the track's own settings, which are returned.
   */
  "camera:applied": {
    request: CameraControlRequest;
    ok: boolean;
    message: string;
    ms: number;
    settings: CameraSettings;
  };
}

/** View → scanner. */
export interface ViewEvents {
  /** Ask for permission and open the preview now; scan automatically once assets are ready. */
  "camera:prepare": void;
  /** Resume a failed boot, reusing assets and model sessions already in memory. */
  "boot:retry": void;
  /** Open the camera and scan. Ignored while a session is running or starting. */
  start: void;
  /** Release the camera. Answered by `session:stopped`. */
  stop: void;
  /** What to scan through. Sent whenever it changes, and on every `session:started`. */
  framing: Framing;
  /**
   * Whether the user is holding the screen — a finger down, or a panel covering the
   * camera. Level-triggered; the scanner decides what to do about it.
   */
  interaction: { active: boolean };
  /** A momentary touch of the UI — a tap, a keystroke, a scroll tick. */
  activity: void;
  /**
   * Change the camera track's own properties. Answered by `camera:applied`, always —
   * with `ok: false` when no camera is open. Requests are applied in order, one at a
   * time; a slider should throttle itself rather than rely on that.
   */
  "camera:control": CameraControlRequest;
  /** Ask for a fresh `camera:capabilities`. Ignored without an open camera. */
  "camera:query": void;
}

export type ScannerEndpoint = Endpoint<ScannerEvents, ViewEvents>;
export type ViewEndpoint = Endpoint<ViewEvents, ScannerEvents>;

/** The link, one endpoint per side. */
export function createLink(): { scanner: ScannerEndpoint; view: ViewEndpoint } {
  const { a, b } = link<ScannerEvents, ViewEvents>();
  return { scanner: a, view: b };
}
